import { ActionIcon, Box, Divider, Group, ScrollArea, Text, Title, Tooltip } from '@mantine/core';
import React from 'react';
import { t } from 'i18next';
import { IconCheckupList, IconTrashX, IconX } from '@tabler/icons-react';
import { hasDivider } from '@/util/hasDivider';
import { useDateFormat } from '@/hooks/useLang';

export interface NotificationType {
  _id: string;
  type: 'success' | 'error' | string;
  case?: string;
  description?: React.ReactNode;
  read: boolean;
  title: React.ReactNode;
  content: {
    errCode: string;
    type: string;
    service: string;
    filename: string;
    label: string;
    productTitle: string;
    _id: string;
  };
  createdAt: string;
}

interface NotificationDrawerProps {
  onClick: (notification: NotificationType) => void;
  onRemoveClick: (notification: NotificationType) => void;
  onReadAll: () => void;
  onDeleteAll: () => void;
  data: NotificationType[];
  fromDrawer?: boolean;
}

export function Notifications({
  onClick,
  onReadAll,
  onDeleteAll,
  data,
  fromDrawer,
  onRemoveClick
}: NotificationDrawerProps) {
  const formatDate = useDateFormat();
  const notifications = data.map((it, idx) => {
    //const Icon = types[it.type] || null;
    //const color = it.read ? 'gray' : it.type === 'error' || it.type === 'info' ? 'red' : undefined;

    return (
      <div key={`${it._id}_${idx}`}>
        <Group
          onClick={() => onClick(it)}
          p={0}
          style={{
            filter: it.read ? 'grayscale(1)' : 'none',
            opacity: it.read ? 0.6 : 1
          }}>
          <div style={{ flex: 1 }}>
            <Text fw="bold" c="accent">
              {it.title}
            </Text>
            <Text size="xs" c="dimmed" opacity={0.6}>
              {formatDate(it.createdAt)}
            </Text>
            <Text fw="bold" c="dimmed" size="sm" style={{ wordBreak: 'break-all' }}>
              {it.description}
            </Text>
          </div>
          <ActionIcon
            onClick={(e) => {
              e.stopPropagation();
              onRemoveClick(it);
            }}
            size={24}
            color="accent"
            radius="xl">
            <IconX />
          </ActionIcon>
        </Group>
        {hasDivider(idx, data) && <Divider my="xs" />}
      </div>
    );
  });

  const buttons = (
    <Group justify="space-between">
      <Title order={4} c="dimmed">{t`Notifications.title`}</Title>
      <div>
        <Tooltip withArrow label={t`Notifications.readAll`}>
          <ActionIcon
            onClick={onReadAll}
            size={36}
            variant="transparent"
            color="gray.7"
            radius="xl">
            <IconCheckupList />
          </ActionIcon>
        </Tooltip>
        <Tooltip withArrow label={t`Notifications.deleteAll`}>
          <ActionIcon
            onClick={onDeleteAll}
            size={36}
            variant="transparent"
            color="gray.7"
            radius="xl">
            <IconTrashX />
          </ActionIcon>
        </Tooltip>
      </div>
    </Group>
  );

  return (
    <>
      {notifications.length ? (
        <>
          {buttons}
          <Divider my="xs" />
          <ScrollArea h={fromDrawer ? 'calc(100dvh - 150px)' : 400} scrollbars="y" offsetScrollbars>
            <Box w={fromDrawer ? 'calc(100vw - 56px)' : 400}>{notifications}</Box>
          </ScrollArea>
        </>
      ) : (
        <Text c="dimmed">{t`Notifications.empty`}</Text>
      )}
    </>
  );
}
